import React from "react";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";

import { License } from "@dashboard/devices/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { showErrorToast } from "@shared/toasts/Toasts";

import { generateLicenses, updateUniversalLicense } from "../../devices/store";

interface UseCreateUniversalLicenseProps {
    licenses: License[];
    universalLicenses: License[];
}

export const useCreateUniversalLicense = (props: UseCreateUniversalLicenseProps) => {
    const { data: workspace } = useCurrentWorkspace();
    const dispatch = useDispatch();

    return React.useCallback(
        async (groupId: number): Promise<string | undefined> => {
            try {
                const existingLicenses = props.universalLicenses.filter((license) => license.universalGroupId === groupId);
                if (existingLicenses.length > 0) {
                    return existingLicenses[0].activationCode;
                }

                const nonUniversalLicense = props.licenses.find(
                    (license) => !license.universalGroupId && !license.universalGroupName,
                );

                let licenseIdToUpdate = nonUniversalLicense?.id;

                if (!licenseIdToUpdate) {
                    if (!workspace?.defaultGroupId) {
                        showErrorToast(i18next.t("modals:generateQrCode.toast.errorCreatingUniversalLicense"));
                        return undefined;
                    }

                    try {
                        const generatedLicenses = await dispatch(
                            generateLicenses({ groupId: workspace.defaultGroupId, grantCount: 1 }),
                        ).unwrap();

                        if (!generatedLicenses || generatedLicenses.length === 0) {
                            showErrorToast(i18next.t("modals:generateQrCode.toast.errorCreatingUniversalLicense"));
                            return undefined;
                        }

                        licenseIdToUpdate = generatedLicenses[0].id;
                    } catch (generateError) {
                        const error = generateError as Error;
                        showErrorToast(error.message || i18next.t("modals:generateQrCode.toast.errorCreatingUniversalLicense"));
                        return undefined;
                    }
                }

                const result = await dispatch(updateUniversalLicense({ id: licenseIdToUpdate, groupId: groupId })).unwrap();
                return result.model.activationCode;
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || i18next.t("modals:generateQrCode.toast.errorCreatingUniversalLicense"));
                return undefined;
            }
        },
        [props.licenses, props.universalLicenses, dispatch, workspace],
    );
};
